import { AuthUser } from '../types';
import { AuthService } from '../services/auth';
import { SubscriptionManager } from './subscription-manager';
import { TreasurePurchase } from './treasure-purchase';
import { TreasureInventory } from './treasure-inventory';

export class DashboardComponent {
  private authService: AuthService;
  private container: HTMLElement;
  private currentUser: AuthUser;
  private subscriptionManager: SubscriptionManager | null = null;
  private treasurePurchase: TreasurePurchase | null = null;
  private treasureInventory: TreasureInventory | null = null;
  private isRefreshing = false;

  constructor(authService: AuthService, container: HTMLElement, user: AuthUser) {
    this.authService = authService;
    this.container = container;
    this.currentUser = user;
  }

  render(): void {
    this.container.innerHTML = `
      <div class="dashboard">
        <header class="dashboard-header">
          <div>
            <h2>💎 Partner Portal</h2>
            <p>Signed in as: ${this.currentUser.email}</p>
          </div>
          <div class="header-actions">
            <button id="refresh-btn" class="refresh-btn" title="Refresh all data">🔄 Refresh</button>
            <button id="signout-btn">Sign Out</button>
          </div>
        </header>
        <div class="dashboard-content">
          <div id="subscription-container"></div>
          <div id="partner-features" class="welcome-message" style="display: none;">
            <h3>🚀 Partner Features</h3>
            <p>Once your subscription is active, you'll be able to:</p>
            <ul>
              <li>🎯 Manage your business treasures and rewards</li>
              <li>💰 Set discount values and reward amounts</li>
              <li>📊 Track coupon usage and analytics</li>
              <li>🏢 Update your business information</li>
            </ul>
          </div>
          <div id="treasure-section" style="display: none;">
            <h3>💎 Merchant Token Manager</h3>
            <div id="purchase-section"></div>
            <div id="inventory-section"></div>
          </div>
        </div>
      </div>
    `;

    this.attachEventListeners();
    this.initializeSubscriptionManager();
  }

  private attachEventListeners(): void {
    const signOutBtn = this.container.querySelector('#signout-btn') as HTMLButtonElement;
    const refreshBtn = this.container.querySelector('#refresh-btn') as HTMLButtonElement;
    
    signOutBtn.addEventListener('click', async () => {
      try {
        await this.authService.signOut();
      } catch (error) {
        console.error('Sign out failed:', error);
      }
    });
    
    refreshBtn.addEventListener('click', async () => {
      refreshBtn.disabled = true;
      const originalText = refreshBtn.textContent;
      refreshBtn.textContent = '🔄 Refreshing...';
      
      try {
        await this.refreshAllData();
      } finally {
        refreshBtn.disabled = false;
        refreshBtn.textContent = originalText;
      }
    });
  }

  private initializeSubscriptionManager(): void {
    const subscriptionContainer = this.container.querySelector('#subscription-container') as HTMLElement;
    if (subscriptionContainer) {
      // Initialize partner features as visible by default (for non-subscribers)
      this.updatePartnerFeaturesVisibility(false);
      
      // We need partner data for subscription manager - for now use placeholder
      this.subscriptionManager = new SubscriptionManager(
        subscriptionContainer,
        this.currentUser.id,
        this.currentUser.email,
        'Partner Business', // This should come from partner data
        (isActive: boolean) => this.updatePartnerFeaturesVisibility(isActive)
      );
    }
  }

  private updatePartnerFeaturesVisibility(isActive: boolean): void {
    const partnerFeatures = this.container.querySelector('#partner-features') as HTMLElement;
    const treasureSection = this.container.querySelector('#treasure-section') as HTMLElement;
    
    if (partnerFeatures) {
      // Show partner features section only for unsubscribed users
      partnerFeatures.style.display = isActive ? 'none' : 'block';
    }
    
    if (treasureSection) {
      // Show treasure section only for subscribed users
      treasureSection.style.display = isActive ? 'block' : 'none';
      
      if (isActive) {
        this.initializeTreasureComponents();
      }
    }
  }

  private initializeTreasureComponents(): void {
    try {
      // Initialize treasure purchase component
      this.treasurePurchase = new TreasurePurchase();
      const purchaseSection = this.container.querySelector('#purchase-section') as HTMLElement;
      if (purchaseSection) {
        purchaseSection.innerHTML = ''; // Clear any existing content
        const purchaseButton = this.treasurePurchase.renderPurchaseButton(this.currentUser.id);
        purchaseSection.appendChild(purchaseButton);
      }

      // Initialize treasure inventory component
      this.treasureInventory = new TreasureInventory();
      const inventorySection = this.container.querySelector('#inventory-section') as HTMLElement;
      if (inventorySection) {
        inventorySection.innerHTML = ''; // Clear any existing content
        const inventory = this.treasureInventory.renderInventory(
          this.currentUser.id,
          () => {
            console.log('Treasure inventory reloaded');
          }
        );
        inventorySection.appendChild(inventory);
      }
    } catch (error) {
      console.error('Failed to initialize treasure components:', error);
    }
  }

  public async refreshSubscription(): Promise<void> {
    if (this.subscriptionManager) {
      await this.subscriptionManager.refresh();
    }
  }

  public async refreshAllData(): Promise<void> {
    if (this.isRefreshing) {
      console.log('Refresh already in progress, skipping...');
      return;
    }

    console.log('Refreshing all dashboard data...');
    this.isRefreshing = true;
    
    // Show loading indicator
    const existingIndicator = this.container.querySelector('.refresh-indicator');
    if (existingIndicator) {
      existingIndicator.remove();
    }
    
    const refreshIndicator = document.createElement('div');
    refreshIndicator.className = 'refresh-indicator';
    refreshIndicator.innerHTML = '🔄 Refreshing data...';
    refreshIndicator.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #4CAF50;
      color: white;
      padding: 10px 20px;
      border-radius: 5px;
      z-index: 1000;
      font-size: 14px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    `;
    document.body.appendChild(refreshIndicator);
    
    try {
      // Refresh subscription status first
      if (this.subscriptionManager) {
        await this.subscriptionManager.refresh();
      }
      
      // Refresh treasure inventory if it exists
      if (this.treasureInventory) {
        const inventorySection = this.container.querySelector('#inventory-section') as HTMLElement;
        if (inventorySection) {
          await this.treasureInventory.refreshInventory(this.currentUser.id, inventorySection);
        }
      }
      
      // Re-enable purchase button if it was disabled
      if (this.treasurePurchase) {
        const purchaseSection = this.container.querySelector('#purchase-section') as HTMLElement;
        if (purchaseSection) {
          purchaseSection.innerHTML = '';
          const freshPurchaseButton = this.treasurePurchase.renderPurchaseButton(this.currentUser.id);
          purchaseSection.appendChild(freshPurchaseButton);
        }
      }
      
      console.log('Dashboard data refresh completed');
      
      // Update indicator to show success
      refreshIndicator.innerHTML = '✅ Data refreshed!';
      refreshIndicator.style.background = '#4CAF50';
      
    } catch (error) {
      console.error('Failed to refresh dashboard data:', error);
      
      // Update indicator to show error
      refreshIndicator.innerHTML = '❌ Refresh failed';
      refreshIndicator.style.background = '#f44336';
    } finally {
      this.isRefreshing = false;
    }
    
    // Remove indicator after 3 seconds
    setTimeout(() => {
      if (refreshIndicator.parentNode) {
        refreshIndicator.parentNode.removeChild(refreshIndicator);
      }
    }, 3000);
  }
}
